import { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";
import { fetchPeplinkGraph } from "../../services/vesselService";

const SplineAreaChartPeplinkQuality = ({ id, type, data, dispatch, navigate }) => {
	const [selectedRange, setSelectedRange] = useState("24");
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isDate, setIsDate] = useState({
		startDate: "",
		endDate: "",
	});
	const token = useSelector((state) => state.auth.user.token);

	useEffect(() => {
		if (data) {
			setIsLoading(false);
		}
	}, [data]);

	// Current date
	const now = new Date();

	const timeRanges = {
		3: "Last 3 Hours",
		6: "Last 6 Hours",
		12: "Last 12 Hours",
		24: "Last 24 Hours",
	};

	const nowTimestamp = Date.now(); // current time in ms

	// If data is empty, create a dummy series for today
	const isEmptyData = !data || data.length === 0;
	const startOfDay = new Date();
	startOfDay.setHours(0, 0, 0, 0);
	const endOfDay = new Date();
	endOfDay.setHours(23, 59, 59, 999);

	const series = [
		{
			name: "Strength (rsrp)",
			data: isEmptyData
				? [
						{ x: startOfDay.getTime(), y: null },
						{ x: endOfDay.getTime(), y: null },
					]
				: data.map((entry) => ({
						x: entry?.timestamp ? entry.timestamp * 1000 : nowTimestamp,
						y: entry?.signal_rsrp ?? null,
					})),
		},
		{
			name: "Quality (RSRQ)",
			data: isEmptyData
				? [
						{ x: startOfDay.getTime(), y: null },
						{ x: endOfDay.getTime(), y: null },
					]
				: data.map((entry) => ({
						x: entry?.timestamp ? entry.timestamp * 1000 : nowTimestamp,
						y: entry?.quality_rsrq ?? null,
					})),
		},
		{
			name: "Quality (SINR)",
			data: isEmptyData
				? [
						{ x: startOfDay.getTime(), y: null },
						{ x: endOfDay.getTime(), y: null },
					]
				: data.map((entry) => ({
						x: entry?.timestamp ? entry.timestamp * 1000 : nowTimestamp,
						y: entry?.quality_sinr ?? null,
					})),
		},
		{
			name: "Latency",
			data: isEmptyData
				? [
						{ x: startOfDay.getTime(), y: null },
						{ x: endOfDay.getTime(), y: null },
					]
				: data.map((entry) => ({
						x: entry?.timestamp ? entry.timestamp * 1000 : nowTimestamp,
						y: entry?.Latency ?? null,
					})),
		},
	];

	const options = {
		chart: {
			type: "area",
			height: 350,
			zoom: { enabled: false },
			toolbar: { show: false },
		},
		dataLabels: { enabled: false },
		stroke: { curve: "smooth" },
		xaxis: {
			type: "datetime",
			labels: {
				formatter: (value) => {
					const date = new Date(value);

					// If a short range (hours), show time (HH:mm)
					if (["3", "6", "12", "24"].includes(selectedRange)) {
						return date.toLocaleTimeString([], {
							hour: "2-digit",
							minute: "2-digit",
							hour12: false, // 24h format
						});
					}

					// Otherwise show full date and time (dd MMM yyyy HH:mm)
					return date.toLocaleString([], {
						day: "2-digit",
						month: "short",
						year: "numeric",
						hour: "2-digit",
						minute: "2-digit",
						hour12: false, // 24h format
					});
				},
				style: {
					colors: "#FFFFFF",
					fontSize: "12px",
					fontWeight: "bold",
				},
			},
			axisBorder: { show: true, color: "#FFFFFF", height: 2 },
			axisTicks: { show: true, color: "#FFFFFF", height: 6 },
		},
		yaxis: {
			title: {
				text: "Signal/Quality Values",
				style: { color: "#FFFFFF", fontSize: "12px", fontWeight: "bold" },
			},
			labels: {
				formatter: (value) => (value !== null && value !== undefined ? value.toFixed(0) : ""),
				style: { colors: "#FFFFFF", fontSize: "12px", fontWeight: "bold" },
			},
			axisBorder: { show: true, color: "#FFFFFF", width: 2 },
			axisTicks: { show: true, color: "#FFFFFF", width: 6 },
		},
		tooltip: {
			x: {
				format: ["3", "6", "12", "24"].includes(selectedRange) ? "HH:mm" : "dd MMM yyyy HH:mm",
			},
		},
		legend: { position: "top", horizontalAlign: "right" },
	};

	// const options = {
	//   chart: {
	//     type: "area",
	//     height: 350,
	//     zoom: {
	//       enabled: false,
	//     },
	//     toolbar: {
	//       show: false, // Hide the toolbar
	//     },
	//   },
	//   dataLabels: {
	//     enabled: false,
	//   },
	//   stroke: {
	//     curve: "smooth",
	//   },
	//   xaxis: {
	//     type: "datetime",
	//     labels: {
	//       formatter: (value) => new Date(value).toLocaleDateString(),
	//       style: {
	//         colors: "#FFFFFF", // White label color for X-axis
	//         fontSize: "12px",
	//         fontWeight: "bold", // Bold X-axis labels
	//       },
	//     },
	//     axisBorder: {
	//       show: true,
	//       color: "#FFFFFF", // White X-axis border
	//       height: 2,
	//     },
	//     axisTicks: {
	//       show: true,
	//       color: "#FFFFFF", // White X-axis ticks
	//       height: 6,
	//     },
	//   },
	//   yaxis: {
	//     title: {
	//       text: "Signal/Quality Values",
	//       style: {
	//         color: "#FFFFFF", // White title color for Y-axis
	//         fontSize: "12px",
	//         fontWeight: "bold", // Bold Y-axis title
	//       },
	//     },
	//     labels: {
	//       formatter: (value) =>
	//         value !== null && value !== undefined ? value.toFixed(0) : "",
	//       style: {
	//         colors: "#FFFFFF", // White label color for Y-axis
	//         fontSize: "12px",
	//         fontWeight: "bold", // Bold Y-axis labels
	//       },
	//     },
	//     axisBorder: {
	//       show: true,
	//       color: "#FFFFFF", // White Y-axis border
	//       width: 2,
	//     },
	//     axisTicks: {
	//       show: true,
	//       color: "#FFFFFF", // White Y-axis ticks
	//       width: 6,
	//     },
	//   },
	//   tooltip: {
	//     x: {
	//       format: "dd MMM yyyy",
	//     },
	//   },
	//   legend: {
	//     position: "top",
	//     horizontalAlign: "right",
	//   },
	// };

	const toggleDropdown = () => {
		setIsDropdownOpen(!isDropdownOpen);
	};

	const handleSelectRange = async (range) => {
		setSelectedRange(range);
		setIsDropdownOpen(false);
		setIsLoading(true);
		try {
			await fetchPeplinkGraph(
				token,
				id,
				range,
				null,
				null,
				"Quality",
				"Cellular Signal Quality",
				dispatch,
				navigate,
			);
		} catch (error) {
			console.error("Error fetching data:", error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleValueChange = async (newDate) => {
		setIsDate(newDate);
		setIsLoading(true);

		try {
			await fetchPeplinkGraph(
				token,
				id,
				null,
				newDate.startDate,
				newDate.endDate,
				"Quality",
				type,
				dispatch,
				navigate,
			);
		} catch (error) {
			console.error("Error fetching data:", error);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div id={`${id}-${type}-chart`} className="w-full lg:w-auto">
			<div className="flex w-full flex-col items-center gap-4 md:flex-row">
				<div className="relative">
					<button
						id="dropdownDefaultButton"
						onClick={toggleDropdown}
						className="inline-flex w-full items-center rounded-lg bg-blue-gray px-5 py-2.5 text-center font-medium text-sm text-white hover:bg-dark-blue focus:outline-none focus:ring-4 focus:ring-blue-800 md:w-auto"
						type="button"
					>
						{timeRanges[selectedRange]}
						<svg
							className="ml-3 h-2.5 w-2.5"
							aria-hidden="true"
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 10 6"
						>
							<path
								stroke="currentColor"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="2"
								d="m1 1 4 4 4-4"
							/>
						</svg>
					</button>

					{isDropdownOpen && (
						<div
							id="dropdown"
							className="absolute z-10 mt-1 w-full divide-y divide-gray-100 rounded-lg bg-dark-blue shadow md:w-36"
						>
							<ul className="py-2 text-gray-200 text-sm">
								{Object.keys(timeRanges).map((range) => (
									<li key={range}>
										<a
											href="#"
											className="block px-2 py-2 hover:bg-blue-gray hover:text-white"
											onClick={() => handleSelectRange(range)}
										>
											{timeRanges[range]}
										</a>
									</li>
								))}
							</ul>
						</div>
					)}
				</div>

				{/* <div className="w-full md:w-auto">
          <Datepicker
            inputClassName="text-white placeholder-white bg-gray-600 hover:bg-gray-700 focus:ring-4 focus:outline-none focus:ring-blue-800 font-medium rounded-lg text-sm px-5 py-2.5 w-full md:w-auto"
            value={isDate}
            popoverDirection="right"
            onChange={handleValueChange}
            showFooter={true}
            showShortcuts={true}
            configs={{
              shortcuts: {
                today: "today",
              },
            }}
            placeholder="Select period"
          />
        </div> */}
			</div>

			{isLoading ? (
				<div className="mt-4 text-center">
					<p>Loading...</p>
				</div>
			) : (
				<ReactApexChart options={options} series={series} type="area" height={350} />
			)}
		</div>
	);
};

export default SplineAreaChartPeplinkQuality;
